import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Calendar, Clock, ArrowLeft, Tag, Share2 } from 'lucide-react';
import { useData } from '../../contexts/DataContext';
import { Loader } from '../../icons/Loader';
import AnimatedEmptyState from '../../components/ui/AnimatedEmptyState';

const BlogPost = () => {
  const { slug } = useParams();
  const { blogs, getBlogBySlug, incrementBlogViews } = useData();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [fetchError, setFetchError] = useState(false);

  useEffect(() => {
    let cancelled = false;

    const fetchPost = async () => {
      setLoading(true);
      setFetchError(false);

      const contextPost = blogs?.find((b) => b.slug === slug && b.status === 'published');

      if (contextPost) {
        if (!cancelled) {
          setPost(contextPost);
          if (contextPost.id) await incrementBlogViews(contextPost.id);
          setLoading(false);
        }
        return;
      }

      try {
        const result = await getBlogBySlug(slug);
        if (cancelled) return;

        if (result.success && result.blog && result.blog.status === 'published') {
          setPost(result.blog);
          if (result.blog.id) await incrementBlogViews(result.blog.id);
        } else {
          setFetchError(true);
        }
      } catch {
        if (!cancelled) setFetchError(true);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    fetchPost();
    return () => {
      cancelled = true;
    };
  }, [slug, blogs, getBlogBySlug, incrementBlogViews]);

  if (loading) {
    return (
      <div className="pf-page-wrap pb-16 flex items-center justify-center min-h-[60vh]">
        <Loader variant="dog" label="Loading article" />
      </div>
    );
  }

  if (!post || fetchError) {
    return (
      <div className="pf-page-wrap pb-16">
        <div className="max-w-2xl mx-auto px-4 py-20">
          <AnimatedEmptyState
            icon="loader-running-dog"
            title="Article not found"
            description="This post may have been removed or the link is incorrect."
            action={
              <Link to="/blog" className="pf-btn-primary inline-flex items-center gap-2">
                <ArrowLeft className="w-4 h-4" />
                Back to blog
              </Link>
            }
          />
        </div>
      </div>
    );
  }

  const handleShare = async () => {
    const url = window.location.href;
    if (navigator.share) {
      try {
        await navigator.share({ title: post.title, url });
      } catch {
        /* user cancelled */
      }
    } else {
      await navigator.clipboard.writeText(url);
    }
  };

  return (
    <div className="pf-page-wrap pb-16">
      <div className="relative h-72 md:h-96 bg-[var(--pf-ink)] overflow-hidden">
        {post.image ? (
          <img src={post.image} alt="" className="w-full h-full object-cover opacity-85" />
        ) : null}
        <div className="absolute inset-0 bg-gradient-to-t from-[var(--pf-ink)]/80 via-[var(--pf-ink)]/20 to-transparent" />
      </div>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 -mt-24 md:-mt-32 relative z-10">
        <Link
          to="/blog"
          className="inline-flex items-center gap-2 text-white/90 hover:text-white mb-6 font-medium text-sm"
        >
          <ArrowLeft className="w-5 h-5" />
          Back to blog
        </Link>

        <article className="pf-organic-card p-6 md:p-12">
          <div className="flex items-center gap-2 mb-4">
            <Tag className="w-4 h-4 text-[var(--pf-accent)]" />
            <span className="text-[var(--pf-accent)] text-sm font-semibold">{post.category}</span>
          </div>

          <h1 className="pf-display text-3xl md:text-5xl text-[var(--pf-ink)] mb-6 leading-tight">
            {post.title}
          </h1>

          <div className="flex flex-wrap items-center gap-4 text-[var(--pf-ink-muted)] mb-8 pb-8 border-b border-[var(--pf-border)]">
            {post.author ? <span className="font-medium text-[var(--pf-ink)]">By {post.author}</span> : null}
            <span className="flex items-center gap-2">
              <Calendar className="w-4 h-4" />
              {new Date(post.date).toLocaleDateString('en-US', {
                month: 'long',
                day: 'numeric',
                year: 'numeric',
              })}
            </span>
            {post.readTime ? (
              <span className="flex items-center gap-2">
                <Clock className="w-4 h-4" />
                {post.readTime}
              </span>
            ) : null}
            {post.views > 0 ? (
              <span className="text-sm">{post.views.toLocaleString()} views</span>
            ) : null}
            <button
              type="button"
              onClick={handleShare}
              className="ml-auto flex items-center gap-2 text-[var(--pf-accent)] hover:opacity-80 font-medium"
            >
              <Share2 className="w-4 h-4" />
              Share
            </button>
          </div>

          <div
            className="prose prose-lg max-w-none prose-headings:text-[var(--pf-ink)] prose-p:text-[var(--pf-ink-muted)] prose-a:text-[var(--pf-accent)] prose-strong:text-[var(--pf-ink)]"
            dangerouslySetInnerHTML={{ __html: post.content }}
          />

          <div className="mt-12 pt-8 border-t border-[var(--pf-border)]">
            <div className="pf-organic-card pf-organic-card--dark p-8 text-center">
              <h3 className="text-2xl font-bold text-white mb-3">Ready to find a pet caregiver?</h3>
              <p className="text-blue-100/80 mb-6 max-w-md mx-auto">
                Browse local caregivers and find the perfect match for your pet
              </p>
              <Link to="/search" className="inline-flex items-center gap-2 bg-white text-[var(--pf-accent)] px-8 py-3 rounded-full font-semibold hover:bg-blue-50 transition-colors">
                Find caregivers
              </Link>
            </div>
          </div>
        </article>

        <div className="mt-12 mb-8">
          <Link to="/blog" className="pf-organic-card block p-6 hover:border-[var(--pf-accent)] transition-colors group">
            <h4 className="font-bold text-lg text-[var(--pf-ink)] mb-2 group-hover:text-[var(--pf-accent)]">
              View all articles
            </h4>
            <p className="text-[var(--pf-ink-muted)] text-sm">Explore more pet care tips and guides</p>
          </Link>
        </div>
      </div>
    </div>
  );
};

export default BlogPost;
